import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft, FiGrid, FiPlusCircle, FiUpload,
  FiList, FiCheckSquare, FiBarChart2, FiSettings, FiLogOut, FiUsers,
} from 'react-icons/fi';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../Styles/Journal.css';

import {
  getToken, setToken, clearToken, getCurrentUser, isTokenExpired,
  canApprove, canAccessConfig, canManageUsers, canSubmitEntry,
  getUserDisplayName,
} from '../../utils/journalApi';

import JournalLogin      from './JournalLogin';
import JournalDashboard  from './JournalDashboard';
import JournalEntryNew   from './JournalEntryNew';
import JournalBulkUpload from './JournalBulkUpload';
import JournalEntryList  from './JournalEntryList';
import JournalApprovals  from './JournalApprovals';
import JournalReports    from './JournalReports';
import JournalConfig     from './JournalConfig';
import JournalUsers      from './JournalUsers';

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit:    { opacity: 0, y: -10, transition: { duration: 0.16, ease: 'easeIn' } },
};

export default function JournalApp() {
  const navigate = useNavigate();
  const [authed, setAuthed]       = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser]           = useState(null);

  useEffect(() => {
    // Azure AD backend redirect callback: ?access_token=<jwt>&token_type=bearer
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('access_token');
    if (urlToken) {
      setToken(urlToken);
      window.history.replaceState({}, '', window.location.pathname);
    }

    const stored = getToken();
    if (stored && !isTokenExpired()) {
      setAuthed(true);
      setUser(getCurrentUser());
    } else {
      clearToken();
    }
  }, []);

  // Listen for 401 forced logout from API interceptor
  useEffect(() => {
    const handleLogout = () => { setAuthed(false); setUser(null); };
    window.addEventListener('journal:logout', handleLogout);
    return () => window.removeEventListener('journal:logout', handleLogout);
  }, []);

  const handleLogin = () => {
    setAuthed(true);
    setUser(getCurrentUser());
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    setUser(null);
  };

  if (!authed) return <JournalLogin onLogin={handleLogin} />;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard',   icon: <FiGrid size={14} />,       show: true },
    { id: 'entries',   label: 'Entries',     icon: <FiList size={14} />,        show: true },
    { id: 'new',       label: 'New Entry',   icon: <FiPlusCircle size={14} />,  show: canSubmitEntry() },
    { id: 'bulk',      label: 'Bulk Upload', icon: <FiUpload size={14} />,      show: canSubmitEntry() },
    { id: 'approvals', label: 'Approvals',   icon: <FiCheckSquare size={14} />, show: canApprove() },
    { id: 'reports',   label: 'Reports',     icon: <FiBarChart2 size={14} />,   show: true },
    { id: 'users',     label: 'Users',       icon: <FiUsers size={14} />,       show: canManageUsers() },
    { id: 'config',    label: 'Config',      icon: <FiSettings size={14} />,    show: canAccessConfig() },
  ].filter(t => t.show);

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard': return <JournalDashboard onNavigate={setActiveTab} />;
      case 'entries':   return <JournalEntryList  onNavigate={setActiveTab} />;
      case 'new':       return canSubmitEntry() ? <JournalEntryNew onNavigate={setActiveTab} /> : null;
      case 'bulk':      return canSubmitEntry() ? <JournalBulkUpload /> : null;
      case 'approvals': return canApprove()     ? <JournalApprovals /> : null;
      case 'reports':   return <JournalReports />;
      case 'users':     return canManageUsers() ? <JournalUsers /> : null;
      case 'config':    return canAccessConfig() ? <JournalConfig /> : null;
      default:          return <JournalDashboard onNavigate={setActiveTab} />;
    }
  };

  const displayName = getUserDisplayName() || user?.sub || 'User';
  const roleName    = (user?.role || '').replace(/_/g, ' ');

  return (
    <motion.div
      className="journal-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      {/* ── Header ── */}
      <motion.div
        className="journal-header"
        initial={{ y: -56, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="journal-header-left">
          <motion.button
            className="journal-back-btn"
            onClick={() => navigate('/home')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiArrowLeft size={14} />
            <span>Back to Home</span>
          </motion.button>
          <motion.div
            className="journal-header-title"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.12, duration: 0.4 }}
          >
            <h1>Journal Posting & Validation</h1>
            <p>Submit · Validate · Approve · Post to SAP</p>
          </motion.div>
        </div>

        <div className="journal-header-right">
          {user && (
            <div style={{ fontSize: '0.82rem', color: 'var(--j-muted)', textAlign: 'right' }}>
              <div style={{ fontWeight: 700, color: 'var(--j-txt)' }}>{displayName}</div>
              <div style={{ fontSize: '0.74rem', textTransform: 'capitalize' }}>{roleName}</div>
            </div>
          )}
          {canSubmitEntry() && (
            <motion.button
              className="journal-btn primary"
              onClick={() => setActiveTab('new')}
              whileTap={{ scale: 0.96 }}
            >
              <FiPlusCircle size={14} /> New Entry
            </motion.button>
          )}
          <motion.button
            className="journal-btn ghost"
            onClick={handleLogout}
            whileTap={{ scale: 0.96 }}
            title="Sign out"
          >
            <FiLogOut size={14} />
          </motion.button>
        </div>
      </motion.div>

      {/* ── Tab bar ── */}
      <motion.div
        className="journal-tab-bar"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18, duration: 0.3 }}
      >
        {tabs.map((tab, i) => (
          <motion.button
            key={tab.id}
            className={`journal-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 + i * 0.05 }}
            whileTap={{ scale: 0.96 }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* ── Content ── */}
      <div className="journal-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {renderTab()}
          </motion.div>
        </AnimatePresence>
      </div>

      <ToastContainer position="top-right" autoClose={4000} hideProgressBar newestOnTop />
    </motion.div>
  );
}
